import { createReducer } from "reduxsauce"
import { put as apiPut } from "api"
import createActions from "helpers/actionCreator"
import { get, join, pick, omit, toLower } from "lodash"
import { takeLatest, select, call, put, all, getContext } from "redux-saga/effects"
import moment from "moment"
import { Types as LoaderTypes } from "model/data"
import { VIEW_TYPES, addBaseToHistory } from "helpers/base"
import { computeUpdatedFilters } from "helpers/filters"
import { createDefaultReducer } from "helpers/reducers"
import { getGroupingType } from "helpers/compositeSecurities"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as compositeSecurityCreators } from "model/compositeSecurity"
import { Creators as RecentInstrumentsCreators } from "../../../reducers/app/recentInstruments"
import { getEarliestDate, getLatestDate, getStartDate } from "./selectors/dates"
import { getAssetName, getSelectedModelId } from "./selectors"
import { getBaseHistory } from "./selectors/base"

export const { Types, Creators } = createActions("ompnt/modules/analyzeCompositeSecurity", {
  toggleFilters: { track: false },
  selectScreen: {
    props: ["screen", "previousSubScreen"],
    track: false,
  },
  setSelectedBase: {
    props: ["selectedBase", "modelId", "account"],
    trackType: "composite-set-selected-base",
    trackPayload: ({ selectedBase }) => pick(selectedBase, "id", "type"),
  },
  saveSelectedBase: {
    props: ["selectedBase", "newHistory"],
    track: false,
  },
  baseSearch: {
    props: ["search"],
    track: false,
  },
  baseInputFocus: { track: false },
  baseInputBlur: { track: false },
  setShowNotHeld: ["showNotHeld"],
  setModel: ["model"],
  setGrouping: {
    props: ["grouping"],
    trackType: "composite-grouping-option-selected",
    trackPayload: ({ grouping }) => {
      let by
      if (grouping) {
        by = get(grouping, "classificationId")
          ? `${grouping.classificationId} - ${grouping.id}` : grouping.id
      }
      return { by }
    },
  },
  setExpand: {
    props: ["expand"],
    trackPayload: ({ expand }) => ({ isExpanded: expand }),
  },
  setDate: ["date"],
  resetDate: [],
  setNormalization: {
    props: ["normalization"],
    trackType: "analyze-trends-normalization-selection",
  },
  setPeriod: ["period"],
  openCompositeSecurity: {
    props: ["security"],
    track: false,
  },
  updateFilters: {
    props: ["filter"],
    track: true,
    trackType: "composite-security-filters-update",
    trackPayload: ({ filter }) => ({ filter }),
  },
}, {
  track: true,
})

const INITIAL_STATE = {
  filtersVisible: false,
  selectedViews: [],
  baseHistory: [],
  previousModelSelectedBaseId: null,
  baseDropdownVisible: false,
  baseSearchValue: "",
  editingFilter: null,
}

const handleToggleFilters = (state) => ({ ...state, filtersVisible: !state.filtersVisible })

const handleBaseSearch = (state, { search }) => ({ ...state, baseSearchValue: search })

const handleBaseInputFocus = (state) => ({ ...state, baseDropdownVisible: true })

const handleBaseInputBlur = (state) => ({ ...state, baseDropdownVisible: false, baseSearchValue: "" })

const handleSaveSelectedBase = (state, { selectedBase, newHistory }) => {
  const newState = {
    baseDropdownVisible: false,
    baseSearchValue: "",
    viewType: selectedBase ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE,
  }

  if (selectedBase) {
    return { ...state, ...newState, baseHistory: newHistory }
  }

  return { ...state, ...newState }
}

function handleUpdateFilters(state, { filter }) {
  return { ...state, ...computeUpdatedFilters(filter) }
}

export const HANDLERS = {
  [Types.TOGGLE_FILTERS]: handleToggleFilters,
  [Types.SAVE_SELECTED_BASE]: handleSaveSelectedBase,
  [Types.BASE_SEARCH]: handleBaseSearch,
  [Types.BASE_INPUT_FOCUS]: handleBaseInputFocus,
  [Types.BASE_INPUT_BLUR]: handleBaseInputBlur,
  [Types.UPDATE_FILTERS]: handleUpdateFilters,
}

export default createReducer(INITIAL_STATE, HANDLERS)

const resetQueryStringState = (state) => omit(state, [
  "disabled",
  "only",
  "factorCategory",
  "contributorCategory",
  "contributorGroup",
  "contributorPage",
  "page",
])

function handleUpdateFiltersQS(state, { filter }) {
  const { filter: updatedFilter } = computeUpdatedFilters(filter)
  return { ...state, filter: updatedFilter }
}

const handleSetGrouping = (state, { grouping }) => ({
  ...state,
  grouping: get(grouping, "id", null),
  groupingType: getGroupingType(grouping),
  contributorGroup: null,
})

const handleSaveSelectedBaseQS = (state, { selectedBase }) => ({
  ...state,
  isActive: selectedBase ? true : null,
  base: get(selectedBase, "id"),
  baseType: get(selectedBase, "type"),
})

const handleSetPeriod = (state, { period: periodChange }) => {
  const { startDate, endDate } = periodChange
  const period = !startDate && !endDate ? periodChange : null

  let { date } = state
  if (date && (date < startDate || date > endDate)) {
    date = null
  }

  return {
    ...state,
    date,
    startDate,
    endDate,
    period,
  }
}

export const queryStringReducers = {
  [Types.SET_MODEL]: createDefaultReducer(),
  [Types.SET_GROUPING]: handleSetGrouping,
  [Types.SET_EXPAND]: createDefaultReducer({ convertBoolFalseToNull: true }),
  [Types.SET_DATE]: createDefaultReducer(),
  [Types.RESET_DATE]: createDefaultReducer({ customStateValues: { date: null } }),
  [Types.SAVE_SELECTED_BASE]: handleSaveSelectedBaseQS,
  [Types.SET_NORMALIZATION]: createDefaultReducer(),
  [Types.SET_PERIOD]: handleSetPeriod,
  [Types.SET_SHOW_NOT_HELD]: createDefaultReducer({ convertBoolFalseToNull: true }),
  [Types.SELECT_SCREEN]: resetQueryStringState,
  [Types.UPDATE_FILTERS]: handleUpdateFiltersQS,
}

function isAssetLoadError(action) {
  return action.type === LoaderTypes.LOAD_ERROR
    && action.resource === "assetDetails"
}

function* handleAssetLoadError(action) {
  const account = yield select(state => get(state, "global.selected.account"))

  if (get(action, "error.code") === 404) {
    const { navigate } = yield getContext("router")
    yield call(navigate, `/${account}/404`, { replace: true })
  }
}

function isAssetLoadSuccess(action) {
  return action.type === LoaderTypes.LOAD_SUCCESS
    && action.resource === "assetDetails"
}

function* handleAssetLoadSuccess(action) {
  const account = yield select(state => get(state, "global.selected.account"))

  const asset = get(action, "data")
  const isEtfWithComposition = get(asset, "assetSubclass") === "etf" && get(asset, "hasComposition")
  const isBasket = get(asset, "assetClass") === "swap"
  const isIndex = get(asset, "indexId")

  const { navigate } = yield getContext("router")
  if (isBasket && !get(asset, "availableFrom")) {
    // this is an empty basket
    yield call(navigate, `/${account}/baskets/${get(asset, "ticker")}/upload`, { replace: true })
  }

  if (!isEtfWithComposition && !isBasket && !isIndex) {
    yield call(navigate, `/${account}/404`, { replace: true })
  }
}

function* handleSelectScreen({ screen }) {
  const { pathname, search } = window.location
  const pathParts = pathname.split("/", 5)

  const { navigate } = yield getContext("router")
  yield call(navigate, `${join(pathParts, "/")}/${screen}${search}`)
}

function* handleSetSelectedBase({ selectedBase, modelId, account }) {
  const [earliestDate, assetName, latestDate, startDate, baseHistory] = yield all([
    select(getEarliestDate),
    select(getAssetName),
    select(getLatestDate),
    select(getStartDate),
    select(getBaseHistory),
  ])

  if (selectedBase) {
    yield put(Creators.saveSelectedBase(selectedBase, addBaseToHistory(baseHistory, selectedBase)))

    const baseStartDate = get(selectedBase, "availableFrom")

    if (startDate < baseStartDate && earliestDate < baseStartDate) {
      yield put(NotificationCreators.add("info", null, `Active Comparison: Now showing active metrics for ${assetName} against ${selectedBase.name} using dates: ${moment(baseStartDate).format("ll")} to ${moment(latestDate).format("ll")}.`))
    } else {
      yield put(NotificationCreators.add("success", null, `Active Comparison: Now showing active metrics for ${assetName} against ${selectedBase.name}.`))
    }
    if (modelId && account) {
      const instrument = yield call(
        apiPut,
        `/api/${account}/recent-instruments?${new URLSearchParams({ modelId })}`,
        { instrumentId: selectedBase.id, instrumentType: toLower(selectedBase.type), searchType: RECENT_INSTRUMENT_SEARCH_TYPES.BASE },
      )
      yield put(RecentInstrumentsCreators.updateRecentInstrument({ type: instrument.type, searchType: RECENT_INSTRUMENT_SEARCH_TYPES.BASE, ...instrument }))
    }
  } else {
    yield put(Creators.saveSelectedBase())
    yield put(NotificationCreators.add("success", null, `Absolute Metrics: Now showing absolute metrics for ${assetName}.`))
  }
}

function* handleOpenCompositeSecurity(action) {
  const [modelId, account] = yield all([
    select(getSelectedModelId),
    select(state => get(state, "global.selected.account")),
  ])
  yield put(compositeSecurityCreators.openCompositeSecurity(account, action.security, true, modelId))
}

export function* saga() {
  yield takeLatest(isAssetLoadError, handleAssetLoadError)
  yield takeLatest(isAssetLoadSuccess, handleAssetLoadSuccess)
  yield takeLatest(Types.SELECT_SCREEN, handleSelectScreen)
  yield takeLatest(Types.SET_SELECTED_BASE, handleSetSelectedBase)
  yield takeLatest(Types.OPEN_COMPOSITE_SECURITY, handleOpenCompositeSecurity)
}
